// src/data/adminClient.js
//
// Client for /api/admin/* endpoints. Admin-only; all requests include
// credentials so the JWT is forwarded to the server's requireRole check.

import { NetworkError, AuthError, errorFromResponse } from "./errors.js";
import { log } from "../utils/logger.js";

async function jsonGet(url) {
  let res;
  try {
    res = await fetch(url, { credentials: "include" });
  } catch (err) {
    log.error("adminClient: network failure", { url, err: String(err) });
    throw new NetworkError(`Failed to reach ${url}`, err);
  }
  if (!res.ok) {
    const e = await errorFromResponse(res);
    log.warn("adminClient: non-2xx", { url, status: res.status, name: e.name });
    throw e;
  }
  try {
    return await res.json();
  } catch (err) {
    throw new NetworkError(`Malformed JSON from ${url}`, err);
  }
}

async function jsonPost(url, body = {}) {
  let res;
  try {
    res = await fetch(url, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
  } catch (err) {
    log.error("adminClient: network failure", { url, err: String(err) });
    throw new NetworkError(`Failed to reach ${url}`, err);
  }
  if (!res.ok) {
    const e = await errorFromResponse(res);
    log.warn("adminClient: non-2xx", { url, status: res.status, name: e.name });
    throw e;
  }
  try {
    return await res.json();
  } catch (err) {
    throw new NetworkError(`Malformed JSON from ${url}`, err);
  }
}

/**
 * Check for accumulated soft-deleted lease rows.
 *
 * @returns {Promise<{
 *   softDeletedCount: number,
 *   warning?: { count: number, ids: string[] }
 * }>}
 * @throws {NetworkError | AuthError}
 */
export async function getSoftDeletedLeasesHealth() {
  return jsonGet("/api/admin/health/soft-deleted-leases");
}

/**
 * Permanently delete all leases where deleted_at IS NOT NULL.
 *
 * @returns {Promise<{ deleted: number }>}
 * @throws {NetworkError | AuthError}
 */
export async function hardDeleteSoftDeletedLeases() {
  return jsonPost("/api/admin/hard-delete-soft-deleted-leases");
}

/**
 * Fetch the next suggested Location ID for every market. Shared by the
 * Location IDs admin tab (display only) and the Add-location modal (used
 * to pre-fill the Location ID field once a market is chosen).
 *
 * @returns {Promise<Array<{ marketId: string, marketName: string, region: string, nextId: string }>>}
 * @throws {NetworkError | AuthError}
 */
export async function getNextLocationIds() {
  return jsonGet("/api/admin/location-ids/next");
}

export async function getNonWorkingDaysAdmin() {
  return jsonGet("/api/admin/non-working-days");
}

export async function createNonWorkingDay(payload) {
  return jsonPost("/api/admin/non-working-days", payload);
}

export async function updateNonWorkingDay(id, payload) {
  return jsonMutation(`/api/admin/non-working-days/${encodeURIComponent(id)}`, "PATCH", payload);
}

export async function deleteNonWorkingDay(id) {
  const url = `/api/admin/non-working-days/${encodeURIComponent(id)}`;
  let res;
  try {
    res = await fetch(url, { method: "DELETE", credentials: "include" });
  } catch (err) {
    log.error("adminClient: network failure", { url, err: String(err) });
    throw new NetworkError(`Failed to reach ${url}`, err);
  }
  if (!res.ok) {
    const e = await errorFromResponse(res);
    log.warn("adminClient: non-2xx", { url, status: res.status, name: e.name });
    throw e;
  }
  if (res.status === 204) return null;
  try {
    return await res.json();
  } catch (err) {
    throw new NetworkError(`Malformed JSON from ${url}`, err);
  }
}

/**
 * Read-only office-to-market integrity report for legacy orphan detection.
 *
 * @returns {Promise<{ok: boolean, count: number, issues: Array}>}
 */
export async function getMarketIntegrityHealth() {
  return jsonGet("/api/admin/health/market-integrity");
}

export async function getExternalLocationMappings(source) {
  const query = source && source !== "all" ? `?source=${encodeURIComponent(source)}` : "";
  return jsonGet(`/api/admin/location-source-map${query}`);
}

export async function createExternalLocationMapping(payload) {
  return jsonPost("/api/admin/location-source-map", payload);
}

export async function updateExternalLocationMapping(id, payload) {
  return jsonMutation(`/api/admin/location-source-map/${encodeURIComponent(id)}`, "PATCH", payload);
}

export async function confirmExternalLocationMapping(id) {
  return jsonPost(`/api/admin/location-source-map/${encodeURIComponent(id)}/confirm`);
}

export async function deleteExternalLocationMapping(id) {
  const url = `/api/admin/location-source-map/${encodeURIComponent(id)}`;
  let res;
  try {
    res = await fetch(url, { method: "DELETE", credentials: "include" });
  } catch (err) {
    log.error("adminClient: network failure", { url, err: String(err) });
    throw new NetworkError(`Failed to reach ${url}`, err);
  }
  if (!res.ok) {
    const e = await errorFromResponse(res);
    log.warn("adminClient: non-2xx", { url, status: res.status, name: e.name });
    throw e;
  }
  if (res.status === 204) return null;
  return res.json();
}

/**
 * Fetch the admin-maintained integration registry. The response contains
 * credential key names only; this client never reads environment variables or
 * Replit Secrets.
 */
export async function getIntegrationRegistry() {
  return jsonGet("/api/admin/integrations");
}

export async function getEnvoyAttendanceFeedHealth() {
  return jsonGet("/api/admin/integrations/envoy/feed-health");
}

export async function getDataReconciliationFindings() {
  return jsonGet("/api/admin/data-reconciliation");
}

export async function getDataReconciliationAlertSummary() {
  return jsonGet("/api/admin/data-reconciliation/summary");
}

export async function acknowledgeDataReconciliationFindings(findingVersion) {
  return jsonPost("/api/admin/data-reconciliation/acknowledge", { findingVersion });
}

export async function getEnvoyAttendanceBackfillStatus() {
  return jsonGet("/api/admin/envoy/attendance-backfill/status");
}

export async function startEnvoyAttendanceBackfillRun(payload = {}) {
  return jsonPost("/api/admin/envoy/attendance-backfill/run", payload);
}

export async function createIntegration(payload) {
  return jsonPost("/api/admin/integrations", payload);
}

export async function syncPlacesUsers() {
  return jsonPost("/api/admin/integrations/places-users/sync", {});
}

async function jsonMutation(url, method, body) {
  let res;
  try {
    res = await fetch(url, {
      method,
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
  } catch (err) {
    log.error("adminClient: network failure", { url, err: String(err) });
    throw new NetworkError(`Failed to reach ${url}`, err);
  }
  if (!res.ok) {
    const e = await errorFromResponse(res);
    log.warn("adminClient: non-2xx", { url, status: res.status, name: e.name });
    throw e;
  }
  try {
    return await res.json();
  } catch (err) {
    throw new NetworkError(`Malformed JSON from ${url}`, err);
  }
}

export async function updateIntegration(id, payload) {
  return jsonMutation(`/api/admin/integrations/${encodeURIComponent(id)}`, "PATCH", payload);
}

export async function deleteIntegration(id) {
  let res;
  const url = `/api/admin/integrations/${encodeURIComponent(id)}`;
  try {
    res = await fetch(url, { method: "DELETE", credentials: "include" });
  } catch (err) {
    log.error("adminClient: network failure", { url, err: String(err) });
    throw new NetworkError(`Failed to reach ${url}`, err);
  }
  if (!res.ok) {
    const e = await errorFromResponse(res);
    log.warn("adminClient: non-2xx", { url, status: res.status, name: e.name });
    throw e;
  }
  try {
    return await res.json();
  } catch (err) {
    throw new NetworkError(`Malformed JSON from ${url}`, err);
  }
}

/**
 * Re-derive the ft/cw/ic distributed-HC split from a Workday CSV.
 *
 * Sends multipart form-data to the admin endpoint. The server updates
 * headcount_distributed_rollup rows for the derived snapshot date.
 *
 * @param {File} file - The Workday Active-Employees CSV file.
 * @param {string} [dateOverride] - Optional YYYY-MM-DD override when the
 *   filename does not encode the snapshot date.
 * @returns {Promise<{
 *   snapshotDate: string,
 *   updated: number,
 *   notFound: number,
 *   totalRows: number,
 *   unmappedRowCount: number,
 *   message: string
 * }>}
 * @throws {NetworkError | AuthError}
 */
/**
 * Fetch soft-deleted items within the 30-day retention window.
 *
 * @param {object} [params]
 * @param {string} [params.type]  Filter by type: "in_focus" | "candidate" | "review" | "lease"
 * @param {string} [params.q]    Search text (name, subtitle, or deletedBy)
 * @returns {Promise<{ items: Array, total: number }>}
 */
export async function getRecentlyDeleted(params = {}) {
  const qs = new URLSearchParams();
  if (params.type && params.type !== "all") qs.set("type", params.type);
  if (params.q)    qs.set("q", params.q);
  const url = `/api/admin/recently-deleted${qs.toString() ? `?${qs}` : ""}`;
  return jsonGet(url);
}

/**
 * Restore a soft-deleted item.
 *
 * @param {string} type  "in_focus" | "candidate" | "review" | "lease"
 * @param {string} id    Item id (item_key for in_focus, UUID for candidate/review, lease id for lease)
 * @returns {Promise<{ ok: boolean, id: string }>}
 */
export async function restoreDeletedItem(type, id) {
  const url = `/api/admin/recently-deleted/${encodeURIComponent(type)}/${encodeURIComponent(id)}/restore`;
  return jsonPost(url);
}

export async function rederiveDistributedSplit(file, dateOverride) {
  const url = dateOverride
    ? `/api/admin/headcount/rederive-distributed-split?date=${encodeURIComponent(dateOverride)}`
    : "/api/admin/headcount/rederive-distributed-split";

  const body = new FormData();
  body.append("file", file);

  let res;
  try {
    res = await fetch(url, { method: "POST", credentials: "include", body });
  } catch (err) {
    log.error("adminClient: network failure", { url, err: String(err) });
    throw new NetworkError(`Failed to reach ${url}`, err);
  }
  if (!res.ok) {
    const e = await errorFromResponse(res);
    log.warn("adminClient: non-2xx", { url, status: res.status, name: e.name });
    throw e;
  }
  try {
    return await res.json();
  } catch (err) {
    throw new NetworkError(`Malformed JSON from ${url}`, err);
  }
}
